"use client";

import { appLinks } from "@/shared/config/appLinks";
import "@/shared/styles/carevinco-public-shell.scss";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useRouter } from "next/navigation";
import { useAppShellReady } from "../hooks/useAppShellReady";
import { LandingAuthQuerySync, useLandingAuthDialog } from "../hooks/useLandingAuthDialog";
import { MarketingFooter } from "@/modules/landing/components/MarketingFooter";
import { InquiryModal } from "@/modules/landing/components/InquiryModal";
import { LandingHeader } from "@/modules/landing/components/LandingHeader";
import AppStartupScreen from "@/shared/components/AppStartupScreen";
import { useAppAuthSnapshot } from "@/shared/providers/AppAuthSnapshot";
import type { AppRole } from "@/shared/store/authStore";

const PublicAuthDialog = lazy(() => import("@/modules/landing/components/PublicAuthDialog"));
const ProviderWireframeFooter = lazy(() => import("@/modules/landing/components/ProviderWireframeFooter"));
const LegacyWireframeFooter = lazy(() => import("@/modules/landing/components/LegacyWireframeFooter"));


type MarketingVariant = "legacy" | "current";

function preferredScrollBehavior(): ScrollBehavior {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

function scrollMarketingHashIntoView(hash: string) {
  const target = document.getElementById(hash.replace("#", ""));
  if (!target) return false;

  const header = document.querySelector(
    ".cv-wireframe .site-header, .cv-wireframe [data-marketing-header]",
  ) as HTMLElement | null;
  const targetTop = window.scrollY + target.getBoundingClientRect().top - (header?.getBoundingClientRect().height ?? 0) - 10;
  window.scrollTo({ top: Math.max(targetTop, 0), behavior: preferredScrollBehavior() });
  return true;
}

function useWireframeInteractions(rootRef: RefObject<HTMLElement | null>, openInquiry: () => void) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    function handleClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      if (target.closest("[data-open-inquiry]")) {
        event.preventDefault();
        openInquiry();
        return;
      }

      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      const href = anchor?.getAttribute("href") ?? "";
      const isLocalHash = href.startsWith("#") || (href.startsWith("/#") && window.location.pathname === "/");
      if (anchor && isLocalHash) {
        const hash = href.startsWith("/#") ? href.slice(1) : href;
        if (scrollMarketingHashIntoView(hash)) {
          event.preventDefault();
          window.history.pushState(null, "", href.startsWith("/#") ? href : `${window.location.pathname}${hash}`);
          return;
        }
      }

    }

    root.addEventListener("click", handleClick);
    return () => root.removeEventListener("click", handleClick);
  }, [openInquiry, rootRef]);
}

function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function updateVisibility() {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      setVisible(scrollableHeight > 0 && window.scrollY / scrollableHeight >= 0.3);
    }

    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility);
    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, []);

  return (
    <button
      aria-label="Back to Top"
      className={`back-to-top${visible ? " is-visible" : ""}`}
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: preferredScrollBehavior() })}
    >
      <span aria-hidden="true" className="back-to-top__icon">↑</span>
      <span className="back-to-top__label">Back to Top</span>
    </button>
  );
}

function WireframeFooter({ role, showInquiry }: { role?: AppRole | null; showInquiry?: boolean } = {}) {
  if (role === "provider") {
    return (
      <Suspense fallback={null}>
        <ProviderWireframeFooter appStoreUrl={appLinks.appStore} googlePlayUrl={appLinks.googlePlay} showInquiry={showInquiry} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <LegacyWireframeFooter appStoreUrl={appLinks.appStore} googlePlayUrl={appLinks.googlePlay} role={role} showInquiry={showInquiry} />
    </Suspense>
  );
}

function WireframeInteractiveShell({
  children,
  includeBackToTop = true,
  variant = "legacy",
}: {
  children: ReactNode;
  includeBackToTop?: boolean;
  variant?: MarketingVariant;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const openInquiry = useCallback(() => setInquiryOpen(true), []);
  const closeInquiry = useCallback(() => setInquiryOpen(false), []);
  useWireframeInteractions(rootRef, openInquiry);

  return (
    <div className={`cv-wireframe${variant === "current" ? " current-public-pages" : ""}`} ref={rootRef}>
      {children}
      <InquiryModal open={inquiryOpen} onClose={closeInquiry} />
      {includeBackToTop ? <BackToTopButton /> : null}
    </div>
  );
}

function WireframePageChrome({ children, variant = "legacy" }: { children: ReactNode; variant?: MarketingVariant }) {
  const appShellReady = useAppShellReady();
  const { hasHydrated, isAuthenticated, isAuthRouting, isLogoutPending, role } = useAppAuthSnapshot();
  const router = useRouter();
  const { authDialogMode, authDialogOpen, authDialogRole, closeAuthDialog, openAuthDialog } = useLandingAuthDialog();

  useEffect(() => {
    if (hasHydrated && isLogoutPending && !isAuthenticated) router.replace("/#home");
  }, [hasHydrated, isAuthenticated, isLogoutPending, router]);

  if (hasHydrated && isAuthenticated && !appShellReady) {
    return <AppStartupScreen message="Preparing your Carevinco experience..." title="Loading Carevinco" />;
  }
  if (hasHydrated && isAuthRouting) {
    return <AppStartupScreen message="Preparing your secure workspace..." title="Loading Carevinco" />;
  }
  if (hasHydrated && isLogoutPending) {
    return <AppStartupScreen message="Taking you to the Carevinco home page..." title="Signing you out" />;
  }

  return (
    <WireframeInteractiveShell variant={variant}>
      <Suspense fallback={null}><LandingAuthQuerySync onAuthRequest={openAuthDialog} /></Suspense>
      <div className={`page-shell page-shell--with-header${variant === "current" ? " current-public-shell" : ""}`}>
        <LandingHeader authDialogOpen={authDialogOpen} onAuthRequest={openAuthDialog} />
        {children}
        {variant === "current" && !isAuthenticated ? (
          <MarketingFooter appStoreUrl={appLinks.appStore} googlePlayUrl={appLinks.googlePlay} />
        ) : (
          <WireframeFooter role={isAuthenticated ? role : undefined} showInquiry={!isAuthenticated} />
        )}
      </div>
      {authDialogOpen ? (
        <Suspense fallback={null}>
          <PublicAuthDialog initialMode={authDialogMode} initialRole={authDialogRole} onClose={closeAuthDialog} />
        </Suspense>
      ) : null}
    </WireframeInteractiveShell>
  );
}

export function MarketingPageShell({ children, variant = "legacy" }: { children: ReactNode; variant?: MarketingVariant }) {
  return <WireframePageChrome variant={variant}>{children}</WireframePageChrome>;
}

export function LandingFooter({
  embedded = false,
  role,
  showInquiry,
}: {
  embedded?: boolean;
  role?: AppRole | null;
  showInquiry?: boolean;
} = {}) {
  const footer = <WireframeFooter role={role} showInquiry={showInquiry} />;
  if (embedded) return footer;

  return (
    <WireframeInteractiveShell includeBackToTop={false}>
      <div className="wireframe-footer-shell">{footer}</div>
    </WireframeInteractiveShell>
  );
}
